import { useState } from 'react';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type ChatAudience = 'public' | 'staff';

/** Contexte d'identité de l'employé connecté (audience 'staff' seulement). */
export interface ChatUserContext {
  firstName: string | null;
  role: string | null;
  accessibleSections: string[];
}

/** Extras passés au stream pour personnaliser l'assistant interne. */
export interface ChatStreamOptions {
  /** Jeton Supabase de l'employé — permet les données réelles (RLS) côté serveur. */
  token?: string | null;
  /** Identité (prénom, rôle, sections accessibles). */
  userContext?: ChatUserContext;
}

/**
 * Envoie l'historique de conversation au backend Netlify Function en
 * STREAMING (SSE). Chaque chunk de texte arrive via le callback `onDelta`.
 * Le hook expose `isPending` (true tant que le stream n'est pas terminé)
 * pour que le widget affiche le typing indicator.
 *
 * Format SSE attendu côté serveur :
 *   data: {"delta":"..."}\n\n   pour chaque chunk
 *   data: [DONE]\n\n             à la fin
 *   data: {"error":"..."}\n\n   sur erreur (throw côté caller)
 */
/**
 * Envoie un feedback 👍 ou 👎 au backend. Best-effort : si l'appel
 * échoue, on log mais on ne throw pas (le UX local a déjà été mis à
 * jour). Pas de Tanstack Query — simple fetch + retour boolean.
 */
export async function sendFeedback(payload: {
  audience: ChatAudience;
  userMessage: string;
  assistantMessage: string;
  rating: 'up' | 'down';
}): Promise<boolean> {
  try {
    const res = await fetch('/.netlify/functions/chat-feedback', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    return res.ok;
  } catch (err) {
    console.warn('[chat-feedback] échec envoi:', err);
    return false;
  }
}

export function useSendChat(audience: ChatAudience = 'public') {
  const [isPending, setIsPending] = useState(false);

  const sendStream = async (
    messages: ChatMessage[],
    onDelta: (delta: string) => void,
    opts?: ChatStreamOptions,
  ): Promise<void> => {
    setIsPending(true);
    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      // Audience 'staff' : on joint le jeton pour les données réelles (RLS).
      if (audience === 'staff' && opts?.token) {
        headers.Authorization = `Bearer ${opts.token}`;
      }
      const res = await fetch('/.netlify/functions/chat', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          messages,
          audience,
          ...(audience === 'staff' && opts?.userContext
            ? { userContext: opts.userContext }
            : {}),
        }),
      });

      // Erreur HTTP : la function retourne un JSON normal (pas un stream).
      if (!res.ok) {
        // Cas particulier : 404 = la function n'est pas déployée (transient
        // pendant un re-deploy Netlify de ~30s). Message plus utile.
        if (res.status === 404) {
          throw new Error(
            'Le service est en train de redémarrer. Réessaie dans 30 secondes.',
          );
        }
        const errPayload = (await res
          .json()
          .catch(() => ({ error: null }))) as { error?: string | null };
        throw new Error(
          errPayload.error ??
            (res.status >= 500
              ? 'Erreur du serveur. Réessaie dans une minute.'
              : `Erreur ${res.status}`),
        );
      }

      if (!res.body) {
        throw new Error('Réponse vide du serveur');
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedAny = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // Découpe en events SSE séparés par double newline.
        const events = buffer.split('\n\n');
        buffer = events.pop() ?? '';

        for (const event of events) {
          const line = event.trim();
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') return;
          try {
            const parsed = JSON.parse(data) as {
              delta?: string;
              error?: string;
            };
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.delta) {
              onDelta(parsed.delta);
              receivedAny = true;
            }
          } catch (parseErr) {
            // Si c'est une vraie erreur retournée par le serveur, on remonte.
            if (parseErr instanceof Error && parseErr.message) {
              throw parseErr;
            }
            // Sinon (JSON malformé d'un chunk partiel), on ignore.
          }
        }
      }

      if (!receivedAny) {
        throw new Error('Réponse vide du serveur');
      }
    } finally {
      setIsPending(false);
    }
  };

  return { sendStream, isPending };
}
